import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, useBlockSlot } from '@vibesites/runtime/react';
import { EditableToken, SectionContextProvider } from '@vibesites/runtime/react';

/**
 * Dietz Property Group override of the prestige ApplyNow section.
 *
 * The parent theme renders two static cards (an "Apply Now" CTA card and a
 * "Need Help? Call Us" card). This override keeps the section heading but
 * replaces those cards with the RealPage Online Leasing (OLL) block so the
 * full online leasing application embeds directly on the page.
 */
export function ApplyNow() {
  const heading = useContentToken('applynow.heading');
  const leasingBlock = useBlockSlot('applynow.leasing_block', {
    defaultValue: 'realpage-oll',
  });

  return (
    <section className="an-section">
      <div className="an-container">
        <EditableToken path="applynow.heading" as="h2" className="sr-only">
          {heading || 'Apply Now'}
        </EditableToken>
        <EditableToken path="applynow.leasing_block" as="div">
          {leasingBlock ? (
            <SectionContextProvider sectionId={leasingBlock.sectionId}>
              <leasingBlock.BlockComponent sectionId={leasingBlock.sectionId} />
            </SectionContextProvider>
          ) : (
            <div className="an-empty">No leasing block selected</div>
          )}
        </EditableToken>
      </div>

      <style>{`
        .an-section {
          padding: 2rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .an-section { padding: 2.5rem 0 4rem; }
        }
        .an-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .an-container { padding: 0 1.5rem; } }

        .an-empty {
          padding: 2.5rem 2rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApplyNow',
  displayName: 'Apply Now',
  tokens: {
    'applynow.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Apply Now',
    },
    'applynow.leasing_block': {
      label: 'Leasing Block',
      type: 'block',
      defaultValue: 'realpage-oll',
      acceptedBlockCategories: ['Integrations'],
    },
  },
};

(ApplyNow as any).contentSchema = contentSchema;

export default ApplyNow;
